import { mergeResultNarrative } from "./resultNarratives";

/**
 * Adaptive Assessment™ result content.
 *
 * The assessment questions, scoring rules, thresholds, and profile assignment
 * live elsewhere. This file controls how an assigned profile is explained and
 * which practical inputs are offered.
 *
 * Result design:
 * - Lead with the reported pattern and the input it needs.
 * - Treat intensity as relative to the individual's current ability.
 * - Offer individual, coached, class, game, and sport examples.
 * - Explain established physical, cognitive, behavioural, and social benefits.
 * - Explain the assessment outcome clearly at the assessment entrance.
 */

export const PRE_ASSESSMENT_DISCLOSURE =
  "The Adaptive Assessment organizes your responses into a personalized nervous-system profile. You will see how activation, recovery, sleep, anxiety, emotion, environment, and recurring behaviour may interact, along with your strengths, current demands, priorities, and practical physiological actions matched to your pattern.";

export const ACTIVITY_MODE_LABELS = {
  acute: "Use today",
  ongoing: "Build into a regular practice",
  both: "Use today or build into a regular practice",
};

export function formatActivityMode(mode) {
  return ACTIVITY_MODE_LABELS[mode] || mode;
}

export const OBSERVATION_QUESTIONS = [
  "Did your energy increase or decrease?",
  "Did physical tension change?",
  "Did your attention become clearer?",
  "Did emotional intensity shift?",
  "Was it easier to settle afterward?",
  "Did social participation improve the experience?",
  "Would you use this option again?",
];

const activity = ({
  id,
  name,
  whyItMayFit,
  mode,
  intensity,
  examples,
  benefits,
  professionalSummary,
  sourceIds,
}) => ({
  id,
  name,
  whyItMayFit,
  mode,
  intensity,
  examples,
  benefits,
  professional: {
    benefitSummary: professionalSummary,
    sourceIds,
  },
});

const ACTIVITY_MAPPING = {
  "high-activation": {
    patternId: "current-app-high-activation",
    selfReportBasis:
      "Lower current-state scores combined with several reported stress-response behaviours.",
    interpretation:
      "Your responses reflect elevated activation: increased urgency, mental scanning, muscular tension, agitation, or difficulty settling. Your system appears to be carrying energy that needs a structured physical outlet.",
    possibleEmotional: [
      "Irritability, anger, fear, or feeling overwhelmed",
      "A strong sense of urgency",
      "Feeling alert but unable to settle",
    ],
    possibleBehavioural: [
      "Pacing, fidgeting, reacting quickly, or avoiding stillness",
      "Scanning the environment or jumping between tasks",
      "Using repetitive behaviours to manage intensity",
    ],
    possibleBody: [
      "Muscular tension",
      "Faster or shallower breathing",
      "Restlessness or a strong urge to move",
    ],
    stateNeed:
      "Give elevated activation a defined physical task. Structured exertion directs urgency into movement, attention, coordination, and effort before transitioning into recovery.",
    recommendedInput: {
      label: "Vigorous physical activity followed by deliberate recovery",
      intensity: "Vigorous — relative to your current ability",
      guide:
        "Choose an effort that feels hard and purposeful for you and that you can perform with control for shorter periods. Pace, resistance, distance, and work-to-rest timing should match your present ability.",
      purpose:
        "Convert unstructured activation into purposeful exertion, then create a clearer transition from effort to recovery.",
    },
    frameworkExplanation:
      "Your reported pattern begins with sustained or demanding inputs, shows up as elevated physiological activation, and influences attention and behaviour. The framework therefore introduces a structured vigorous input to direct that activation, followed by recovery so the next repeated pattern can become more controlled and adaptable.",
    activities: [
      activity({
        id: "vigorous-whole-body-intervals",
        name: "Vigorous whole-body intervals",
        whyItMayFit:
          "Short bouts of whole-body effort give high activation a clear task, directing energy into coordinated movement and cardiorespiratory work.",
        mode: "both",
        intensity: "Vigorous relative to your current ability, using short work periods with recovery between efforts.",
        examples:
          "Fast uphill walking, running, cycling, rowing, swimming, cardio equipment, sled work, or a whole-body conditioning circuit.",
        benefits:
          "Builds cardiorespiratory fitness, muscular endurance, work capacity, attention under effort, and a strong contrast between exertion and recovery.",
        professionalSummary:
          "Vigorous aerobic and interval exercise can improve cardiorespiratory fitness and work capacity. For this profile, the practical value is the combination of whole-body exertion, attentional demand, and a deliberate post-effort recovery transition.",
        sourceIds: [
          "milanovic-2015-hiit-vs-mict-vo2max (PMID 26243014)",
          "who-2020-guidelines (PMID 33239350)",
        ],
      }),
      activity({
        id: "high-movement-skill",
        name: "High-movement structured skill development",
        whyItMayFit:
          "High-movement skill practice combines whole-body exertion, rhythm, coordination, decision-making, and focused attention.",
        mode: "both",
        intensity: "Moderate to vigorous relative to your current ability.",
        examples:
          "Dance, racquet sports, technical movement drills, martial arts, striking drills as one example, or a fast-paced coached circuit.",
        benefits:
          "Develops aerobic and anaerobic fitness, coordination, timing, confidence, skill, concentration, and a productive outlet for physical intensity. A class can also add instruction, shared effort, and camaraderie.",
        professionalSummary:
          "Striking is presented as a practical delivery method for vigorous exercise, coordination, attention, skill development, and group participation. The recommendation is based on those combined inputs rather than a claim that striking is uniquely required.",
        sourceIds: [
          "bozdarov-2022-boxing-mental-health-scoping",
          "who-2020-guidelines (PMID 33239350)",
        ],
      }),
      activity({
        id: "vigorous-group-sport",
        name: "Vigorous group exercise or sport",
        whyItMayFit:
          "Fast-moving group activity combines exertion with external focus, shared rules, communication, and immediate feedback.",
        mode: "ongoing",
        intensity: "Moderate to vigorous relative to your role, pace, and current ability.",
        examples:
          "Circuit classes, basketball, soccer, hockey, racket sports, martial arts, conditioning groups, or other active recreational games.",
        benefits:
          "Supports heart and lung fitness, agility, reaction, decision-making, enjoyment, accountability, social connection, and belonging.",
        professionalSummary:
          "Group exercise and sport combine physical activity with social support, shared effort, skill, and participation. These features can strengthen adherence and add social and behavioural value beyond the exercise dose alone.",
        sourceIds: [
          "golaszewski-2021-group-exercise-social-support",
          "andersen-2019-team-sport-social-psych-health",
        ],
      }),
    ],
    participantSummary:
      "Your responses describe elevated activation that can be directed into purposeful physical effort and followed by a deliberate recovery transition.",
    professionalSummary:
      "The selected input is vigorous activity relative to the individual's ability. Recommended formats combine cardiorespiratory demand, whole-body exertion, attention, coordination, and—where desired—shared effort and camaraderie.",
  },

  "reduced-capacity": {
    patternId: "current-app-reduced-capacity",
    selfReportBasis:
      "Lower reported physical capacity, or lower state combined with lower current inputs.",
    interpretation:
      "Your responses reflect reduced energy, drive, movement, or physical tolerance. The priority is to create enough activation to build momentum without making the starting point unnecessarily difficult.",
    possibleEmotional: [
      "Flatness, sadness, or disconnection",
      "Reduced enthusiasm or motivation",
      "Feeling that ordinary tasks require more effort",
    ],
    possibleBehavioural: [
      "Withdrawing from activity or social contact",
      "Difficulty starting even when you want to",
      "Long periods with very little movement",
    ],
    possibleBody: [
      "Heaviness or fatigue",
      "Low physical energy",
      "Reduced spontaneous movement",
    ],
    stateNeed:
      "Use accessible movement, light exposure, rhythm, and connection to increase activation. Consistency is the primary target; intensity can rise as energy and physical confidence build.",
    recommendedInput: {
      label: "Low-to-moderate rhythmic movement",
      intensity: "Light to moderate — relative to your current ability",
      guide:
        "Choose an effort that feels accessible enough to begin and purposeful enough to change your energy. Start with a duration you can repeat, then build pace or time as capacity improves.",
      purpose:
        "Increase circulation, movement, alertness, and behavioural momentum while rebuilding confidence in physical activity.",
    },
    frameworkExplanation:
      "Lower movement and reduced energy can reinforce one another through the adaptive loop. The framework introduces an accessible physical input first, uses repetition to build capacity, and gradually establishes a more active baseline.",
    activities: [
      activity({
        id: "short-outdoor-walk",
        name: "Short outdoor walk",
        whyItMayFit:
          "Walking is immediately accessible and combines rhythmic movement, circulation, environmental change, and—when outdoors—natural light.",
        mode: "both",
        intensity: "Light to moderate relative to your current ability.",
        examples:
          "A short walk around the block, a park walk, walking to complete an errand, or walking with another person.",
        benefits:
          "Supports heart and lung health, circulation, mobility, metabolism, cognition, mood, routine, and time outdoors. Walking with someone also adds social contact and accountability.",
        professionalSummary:
          "Walking contributes to aerobic activity, mobility, metabolic health, cognition, mood, and routine formation. Outdoor and paired formats can add environmental and social inputs without changing the basic movement.",
        sourceIds: [
          "who-2020-guidelines (PMID 33239350)",
          "Noetel et al. 2024 (PMID 38355154)",
          "hanson-jones-2015-walking-groups-health",
        ],
      }),
      activity({
        id: "steady-aerobic-activation",
        name: "Steady rhythmic aerobic activity",
        whyItMayFit:
          "Continuous rhythmic movement gives the body a repeatable activation signal and allows pace and duration to increase gradually.",
        mode: "ongoing",
        intensity: "Light to moderate relative to your current ability.",
        examples:
          "Walking, cycling, swimming, dancing, elliptical exercise, easy jogging, or low-impact cardio.",
        benefits:
          "Builds aerobic capacity, energy, circulation, movement confidence, sleep support, cognitive function, and tolerance for longer periods of activity.",
        professionalSummary:
          "Regular aerobic exercise supports cardiorespiratory fitness, metabolic health, cognition, mood, and sleep. A progressive steady format gives this profile a repeatable way to expand capacity.",
        sourceIds: [
          "who-2020-guidelines (PMID 33239350)",
          "Noetel et al. 2024 (PMID 38355154)",
        ],
      }),
      activity({
        id: "foundational-strength-group",
        name: "Foundational strength or beginner group activity",
        whyItMayFit:
          "Simple resistance or group movement provides structure, visible progress, and a scheduled reason to engage.",
        mode: "ongoing",
        intensity: "Light to moderate relative to your current ability.",
        examples:
          "Bodyweight exercises, resistance bands, machine-based strength work, a beginner circuit, introductory class, or coached fundamentals.",
        benefits:
          "Develops strength, muscular endurance, movement confidence, functional capacity, routine, competence, and—when done with others—connection and encouragement.",
        professionalSummary:
          "Progressive resistance activity supports strength and functional capacity. Coaching or group participation can add skill development, structure, adherence, and social reinforcement.",
        sourceIds: [
          "schoenfeld-2019-resistance-frequency-hypertrophy (PMID 30558493)",
          "golaszewski-2021-group-exercise-social-support",
        ],
      }),
    ],
    participantSummary:
      "Your responses describe reduced capacity that can be supported by accessible, repeatable movement that increases activation and builds momentum.",
    professionalSummary:
      "The selected input is accessible low-to-moderate movement. The emphasis is on circulation, aerobic development, strength, routine, confidence, environmental exposure, and social reinforcement.",
  },

  "recovery-focus": {
    patternId: "current-app-recovery-focus",
    selfReportBasis:
      "Moderate-to-good reported inputs and capacity with a comparatively lower current-state score.",
    interpretation:
      "Your responses suggest that demand and activity are present, but recovery is not keeping pace. The priority is to improve the transition between effort and restoration.",
    possibleEmotional: [
      "Irritability with reduced motivation",
      "Feeling both restless and depleted",
      "Frustration that effort is not producing the expected energy",
    ],
    possibleBehavioural: [
      "Difficulty settling into either activity or rest",
      "Inconsistent pacing across the day",
      "Continuing to add demand when recovery needs attention",
    ],
    possibleBody: [
      "Fatigue combined with tension",
      "Unrefreshing sleep",
      "Heaviness after otherwise manageable activity",
    ],
    stateNeed:
      "Use low-to-moderate rhythmic activity, mobility, breathing rhythm, sleep consistency, and enjoyable social movement to improve recovery between demands.",
    recommendedInput: {
      label: "Recovery-supportive movement and rhythm",
      intensity: "Light to moderate — relative to your current ability",
      guide:
        "Choose an effort that leaves room for smooth breathing, controlled movement, and a settled finish. The goal is to support recovery rather than create another demanding session.",
      purpose:
        "Improve the transition from activation to recovery and allow previous physical and emotional demands to be consolidated.",
    },
    frameworkExplanation:
      "Your current inputs are producing load, but the feedback and recovery side of the adaptive loop appears under-resourced. The framework therefore strengthens recovery inputs so activity can produce adaptation instead of simply adding more demand.",
    activities: [
      activity({
        id: "recovery-walk-cycle-swim",
        name: "Easy rhythmic aerobic movement",
        whyItMayFit:
          "Continuous low-demand movement supports circulation and rhythm while maintaining a clear recovery emphasis.",
        mode: "both",
        intensity: "Light to moderate relative to your current ability.",
        examples:
          "Easy walking, relaxed cycling, comfortable swimming, light cardio equipment, or a gentle outdoor route.",
        benefits:
          "Supports circulation, aerobic maintenance, mobility, stress reduction, sleep, mental clarity, and active recovery between more demanding periods.",
        professionalSummary:
          "Low-to-moderate aerobic activity supports circulation, aerobic health, sleep, mood, and recovery without requiring the same demand as a hard training session.",
        sourceIds: [
          "who-2020-guidelines (PMID 33239350)",
          "Liao et al. 2026 (PMID 41815256)",
        ],
      }),
      activity({
        id: "mobility-breathing-rhythm",
        name: "Mobility and paced breathing",
        whyItMayFit:
          "Slow, controlled movement paired with an even breathing rhythm creates a deliberate shift away from urgency and toward recovery.",
        mode: "both",
        intensity: "Light and controlled.",
        examples:
          "Mobility sequences, stretching, yoga-based movement, slow breathing practice, or a guided recovery class.",
        benefits:
          "Supports breathing control, body awareness, mobility, muscular relaxation, attention, and a repeatable transition into a calmer pace.",
        professionalSummary:
          "Paced breathing and controlled mobility provide low-demand attentional and respiratory inputs that can support a deliberate recovery routine.",
        sourceIds: [
          "Laborde et al. 2022 (PMID 35623448)",
          "Shao et al. 2024 (DOI 10.1007/s12671-023-02294-2)",
        ],
      }),
      activity({
        id: "social-recovery-activity",
        name: "Low-pressure social movement",
        whyItMayFit:
          "Enjoyable movement with other people can support recovery while adding connection, conversation, and shared routine.",
        mode: "ongoing",
        intensity: "Light to moderate relative to the group and your current ability.",
        examples:
          "Walking with someone, recreational swimming, casual cycling, a mobility class, easy racket games, or non-competitive group recreation.",
        benefits:
          "Combines physical activity with enjoyment, social connection, accountability, belonging, and a lower-pressure reason to keep moving.",
        professionalSummary:
          "Social movement adds support, belonging, and adherence to the physical benefits of light-to-moderate activity. A recreational format can make recovery active and socially reinforcing.",
        sourceIds: [
          "golaszewski-2021-group-exercise-social-support",
          "hanson-jones-2015-walking-groups-health",
        ],
      }),
    ],
    participantSummary:
      "Your responses suggest that recovery needs a stronger place inside the activity cycle so existing effort can produce a better return.",
    professionalSummary:
      "The selected input is recovery-supportive movement and rhythm. The emphasis is on circulation, sleep support, breathing control, mobility, enjoyable participation, and a clearer transition between demand and restoration.",
  },

  "regulation-building": {
    patternId: "current-app-regulation-building",
    selfReportBasis:
      "A mixed or working-range pattern that does not meet the other four profile thresholds.",
    interpretation:
      "Your responses reflect a working baseline with room to make regulation more consistent. The priority is to use repeatable movement, progressive challenge, and supportive environments to strengthen how reliably you respond to demand.",
    possibleEmotional: [
      "A generally workable emotional baseline",
      "Occasional stress responses under changing demand",
      "Interest in making progress feel more consistent",
    ],
    possibleBehavioural: [
      "Ability to engage, with occasional returns to older patterns",
      "Regulation that still requires deliberate effort",
      "Good routines that are not yet fully automatic",
    ],
    possibleBody: [
      "Generally functional energy",
      "Capacity for moderate physical effort",
      "Room to improve recovery and consistency",
    ],
    stateNeed:
      "Use consistent moderate activity, strength, skill development, environmental structure, and social participation to turn regulation into a more reliable default.",
    recommendedInput: {
      label: "Consistent moderate activity with progressive challenge",
      intensity: "Moderate — relative to your current ability",
      guide:
        "Choose a purposeful effort you can repeat consistently. Increase pace, resistance, duration, coordination, or social demand gradually as the activity becomes easier to manage.",
      purpose:
        "Build a wider and more reliable response to physical, emotional, behavioural, and social demands.",
    },
    frameworkExplanation:
      "Your current inputs and capacity are in a working range, while the behavioural output still has room to become more consistent. The framework uses repeatable moderate inputs and progressive challenge to reinforce a steadier adaptation and baseline.",
    activities: [
      activity({
        id: "moderate-aerobic-rhythm",
        name: "Moderate rhythmic aerobic activity",
        whyItMayFit:
          "A repeatable aerobic rhythm builds physical capacity while practising sustained attention and controlled effort.",
        mode: "ongoing",
        intensity: "Moderate relative to your current ability.",
        examples:
          "Brisk walking, jogging, cycling, swimming, rowing, dancing, hiking, or cardio equipment.",
        benefits:
          "Supports heart and lung health, aerobic capacity, metabolism, cognition, mood, sleep, attention, and recovery between demands.",
        professionalSummary:
          "Moderate aerobic activity provides broad cardiovascular, metabolic, cognitive, mood, and sleep benefits. Consistency makes it a useful foundation for expanding adaptive capacity.",
        sourceIds: ["who-2020-guidelines (PMID 33239350)"],
      }),
      activity({
        id: "progressive-strength-skill",
        name: "Progressive strength or skill practice",
        whyItMayFit:
          "Progressive resistance and skill practice create clear feedback, measurable competence, and repeated exposure to manageable challenge.",
        mode: "ongoing",
        intensity: "Moderate relative to your current ability, progressing over time.",
        examples:
          "Resistance training, calisthenics, climbing, martial arts, technical boxing, gymnastics-based skills, or coached movement practice.",
        benefits:
          "Develops strength, muscular endurance, coordination, confidence, attention, competence, and tolerance for progressive demand.",
        professionalSummary:
          "Progressive resistance and skill activity combine physical development with competence, attention, feedback, and structured progression.",
        sourceIds: ["schoenfeld-2019-resistance-frequency-hypertrophy (PMID 30558493)"],
      }),
      activity({
        id: "cooperative-games-sport",
        name: "Group games or recreational sport",
        whyItMayFit:
          "Games and sport add communication, cooperation, decision-making, shared rules, and social feedback to physical activity.",
        mode: "ongoing",
        intensity: "Light to vigorous depending on the activity, scaled to your current ability.",
        examples:
          "Pickleball, basketball, volleyball, soccer, racket sports, recreational leagues, group classes, or cooperative games.",
        benefits:
          "Supports fitness, agility, coordination, reaction, enjoyment, accountability, camaraderie, social connection, and community participation.",
        professionalSummary:
          "Group games and sport combine movement with social support, communication, skill, enjoyment, and belonging. These additional inputs can reinforce participation and behavioural consistency.",
        sourceIds: [
          "eime-2013-sport-participation-psych-social-benefits",
          "andersen-2019-team-sport-social-psych-health",
        ],
      }),
    ],
    participantSummary:
      "Your responses reflect a workable baseline that can become more consistent through repeatable movement, progressive challenge, and supportive social inputs.",
    professionalSummary:
      "The selected input is consistent moderate activity with progressive challenge. The emphasis is on aerobic development, strength, competence, routine, social connection, and the repeated practice of managing demand.",
  },

  "growth-performance": {
    patternId: "current-app-growth-performance",
    selfReportBasis:
      "High current-state, input, and capacity scores with few reported stress-response behaviours.",
    interpretation:
      "Your responses reflect strong current capacity and readiness for greater challenge. The priority is to expand performance while protecting the recovery that makes adaptation possible.",
    possibleEmotional: [
      "Confidence and readiness",
      "Curiosity about greater challenge",
      "Motivation to develop performance",
    ],
    possibleBehavioural: [
      "Consistent engagement",
      "Willingness to take on progressive demand",
      "Ability to return to routine after challenging periods",
    ],
    possibleBody: [
      "Stable energy",
      "Readiness for demanding movement",
      "Capacity to recover between efforts",
    ],
    stateNeed:
      "Use progressive moderate-to-vigorous training, strength, skill, competition, and deliberate recovery to expand capacity without losing consistency.",
    recommendedInput: {
      label: "Progressive moderate-to-vigorous training",
      intensity: "Moderate to vigorous — relative to your current ability",
      guide:
        "Use controlled progression across intensity, volume, skill, or complexity. Hard efforts should be balanced by enough recovery to preserve performance quality and consistency.",
      purpose:
        "Raise physical and behavioural capacity, express existing readiness, and build a higher sustainable performance baseline.",
    },
    frameworkExplanation:
      "Your reported baseline shows the capacity to receive greater demand. The framework therefore increases the training signal while preserving feedback and recovery, allowing repeated challenge to become a higher sustainable level of performance.",
    activities: [
      activity({
        id: "progressive-aerobic-interval-training",
        name: "Progressive aerobic or interval training",
        whyItMayFit:
          "Steady and interval formats provide different ways to increase cardiorespiratory demand and performance capacity.",
        mode: "ongoing",
        intensity: "Moderate to vigorous relative to your current ability.",
        examples:
          "Running, cycling, rowing, swimming, hills, tempo work, interval sessions, or sport conditioning.",
        benefits:
          "Improves heart and lung fitness, aerobic and anaerobic capacity, muscular endurance, pacing, energy management, and recovery between efforts.",
        professionalSummary:
          "Moderate continuous and interval training both support cardiorespiratory development. The format can be selected according to the person's goals, experience, activity, and preferred training environment.",
        sourceIds: [
          "milanovic-2015-hiit-vs-mict-vo2max (PMID 26243014)",
          "who-2020-guidelines (PMID 33239350)",
        ],
      }),
      activity({
        id: "progressive-resistance-training",
        name: "Progressive resistance training",
        whyItMayFit:
          "Resistance training provides a clear path for increasing force, skill, muscular capacity, and tolerance for physical load.",
        mode: "ongoing",
        intensity: "Moderate to vigorous relative to your current ability and experience.",
        examples:
          "Free weights, machines, calisthenics, loaded carries, Olympic-lifting derivatives, or coached strength programming.",
        benefits:
          "Develops strength, muscular endurance, power, movement skill, bone support, metabolic health, confidence, and performance capacity.",
        professionalSummary:
          "Progressive resistance training supports strength, muscular development, physical function, and performance. Volume and frequency can be organised around goals, experience, recovery, and schedule.",
        sourceIds: ["schoenfeld-2019-resistance-frequency-hypertrophy (PMID 30558493)"],
      }),
      activity({
        id: "performance-sport-competition",
        name: "Sport, competition, or advanced group training",
        whyItMayFit:
          "Sport and advanced group training combine physical output with decision-making, skill, communication, and shared performance goals.",
        mode: "ongoing",
        intensity: "Moderate to vigorous relative to the activity and your current ability.",
        examples:
          "Team sport, racket sport, martial arts, racing, competitive recreational leagues, advanced classes, or performance-focused group training.",
        benefits:
          "Develops conditioning, speed, agility, coordination, decision-making, confidence, teamwork, camaraderie, accountability, and performance under pressure.",
        professionalSummary:
          "Sport and group performance formats combine training load with skill, decision-making, teamwork, social support, and meaningful goals. Those combined inputs can support continued engagement and performance expression.",
        sourceIds: [
          "eime-2013-sport-participation-psych-social-benefits",
          "andersen-2019-team-sport-social-psych-health",
        ],
      }),
    ],
    participantSummary:
      "Your responses reflect strong current capacity that can be directed toward progressive performance, skill, and higher sustainable demand.",
    professionalSummary:
      "The selected input is progressive moderate-to-vigorous training. The emphasis is on cardiorespiratory development, strength, skill, performance, recovery, teamwork, and progressively expanding capacity.",
  },
};

export const EVIDENCE_MAPPING = Object.fromEntries(
  Object.entries(ACTIVITY_MAPPING).map(([key, mapping]) => [
    key,
    mergeResultNarrative(key, mapping),
  ])
);

/** Resolve the result content for a given archetype key. */
export function getEvidenceMapping(archetypeKey) {
  return EVIDENCE_MAPPING[archetypeKey] || null;
}
